import { ALLOWED_IMAGE_TYPES, ALLOWED_VIDEO_TYPES } from "@/config/constants";

// Helper function to get dimensions while maintaining aspect ratio.
// Exported for unit tests. "Contain" sizing: fit the LONG side to the box so
// the WHOLE image lands inside maxWidth×maxHeight (aspect preserved). Bubbles
// now render at the media's real aspect and cover-fit the thumbnail, so the
// thumbnail must be the full image — a cover crop here would clip it. Never
// upscales: a source smaller than the box keeps its natural size.
export function getWidthAndHeight(
  originalWidth: number,
  originalHeight: number,
  maxWidth: number,
  maxHeight: number,
) {
  const scale = Math.min(
    maxWidth / originalWidth,
    maxHeight / originalHeight,
    1,
  );

  const width = Math.round(originalWidth * scale);
  const height = Math.round(originalHeight * scale);

  return { width, height };
}

// Bound the whole capture so a stalled media pipeline fails the selection
// (the caller then degrades to a placeholder) instead of hanging. Short: the
// success path is sub-second; anything longer is already failing.
const VIDEO_FRAME_CAPTURE_TIMEOUT_MS = 4_000;

// Redraw attempts when the captured frame comes back blank (see
// isImageDataBlank) before accepting whatever the element has.
const BLANK_FRAME_RETRY_LIMIT = 3;

type VideoWithFrameCallback = HTMLVideoElement & {
  requestVideoFrameCallback?: (cb: () => void) => number;
};

// Bounded frame-wait: requestVideoFrameCallback raced against a short
// timeout. Composition is an OPTIMIZATION HINT here, never a dependency —
// compositors skip painting transparent/off-screen elements, so rVFC may
// legitimately never fire for the invisible capture element (that unbounded
// wait was the fourth-iteration bug: every capture hung to the outer
// timeout on every engine). Correctness comes from the caller's empirical
// pixel-verify, not from any engine event; drawImage reads the DECODED
// frame regardless of compositing.
function waitForFrameOrTimeout(
  video: HTMLVideoElement,
  ms: number,
): Promise<void> {
  return new Promise((resolve) => {
    let settled = false;
    const finish = () => {
      if (settled) {
        return;
      }
      settled = true;
      clearTimeout(timer);
      resolve();
    };
    const timer = setTimeout(finish, ms);
    const v = video as VideoWithFrameCallback;
    if (typeof v.requestVideoFrameCallback === "function") {
      v.requestVideoFrameCallback(finish);
    } else if (video.readyState >= 2) {
      finish();
    } else {
      video.addEventListener("loadeddata", finish, { once: true });
    }
  });
}

/**
 * Detects a blank (uniform) captured frame — every strided sample identical,
 * which covers all-black, all-transparent, and solid-color non-frames. Used
 * to verify capture EMPIRICALLY instead of trusting engine events (WebKit has
 * fired `seeked` without a decodable frame; that shipped blank thumbnails).
 * Exported for unit tests.
 */
export function isImageDataBlank(data: Uint8ClampedArray): boolean {
  if (data.length < 8) {
    return true;
  }
  // ~64 samples, pixel-aligned stride.
  const stride = Math.max(4, Math.floor(data.length / 4 / 64) * 4);
  const [r, g, b, a] = [data[0], data[1], data[2], data[3]];
  for (let i = stride; i + 3 < data.length; i += stride) {
    if (
      data[i] !== r ||
      data[i + 1] !== g ||
      data[i + 2] !== b ||
      data[i + 3] !== a
    ) {
      return false;
    }
  }
  return true;
}

// Creates a video element with a decoded, paintable frame ready for canvas
// capture, plus the cleanup that tears it down. THE CALLER OWNS cleanup()
// (call in `finally`) — the element must stay alive for draw + blank-retry.
//
// Hard-won, engine-proof recipe; three prior versions failed in the field:
//  1. preload="metadata" + immediate seek → WebKit fired `seeked` without a
//     decodable frame → BLANK thumbnails from every iPhone sender.
//  2. preload="auto" + passively waiting for `loadeddata` → iOS never loads
//     media data without an explicit kick → timeout → "error creating
//     thumbnail" blocked every iPhone send.
//  3. The kick + requestVideoFrameCallback on a DETACHED element → an element
//     that is never rendered never composites, so rVFC never fires on ANY
//     engine → every capture timed out everywhere (Chrome included) and every
//     selection ate the full timeout before degrading to a placeholder.
// Therefore: attach the element to the DOM invisibly-but-rendered (offscreen
// fixed position + opacity 0 — NOT display:none, which suppresses rendering),
// explicit load(), muted play() after metadata (iOS's decode kick; never
// needs a gesture), wait for a PRESENTED frame, pause, seek to a
// representative frame, wait again — and let the caller verify pixels.
function createVideoFromFile(
  file: File,
): Promise<{ video: HTMLVideoElement; cleanup: () => void }> {
  return new Promise((resolve, reject) => {
    const video = document.createElement("video");
    const url = URL.createObjectURL(file);
    let done = false;
    let cleaned = false;

    const cleanup = () => {
      if (cleaned) {
        return;
      }
      cleaned = true;
      video.pause();
      // Detach the source properly (WebKit media sessions can outlive the
      // element — same lesson as the viewer teardown), then leave the DOM.
      video.removeAttribute("src");
      try {
        video.load();
      } catch {
        // jsdom / older engines
      }
      video.remove();
      URL.revokeObjectURL(url);
    };

    const finishOk = () => {
      if (done) {
        return;
      }
      done = true;
      clearTimeout(timer);
      video.pause();
      resolve({ video, cleanup });
    };
    const finishErr = (err: unknown) => {
      if (done) {
        return;
      }
      done = true;
      clearTimeout(timer);
      cleanup();
      reject(err);
    };

    const timer = setTimeout(
      () => finishErr(new Error("video frame capture timed out")),
      VIDEO_FRAME_CAPTURE_TIMEOUT_MS,
    );

    video.preload = "auto";
    video.muted = true;
    video.playsInline = true;
    video.controls = false;
    // Invisible but RENDERED — composition is what makes frames (and rVFC) real.
    video.style.position = "fixed";
    video.style.left = "-9999px";
    video.style.top = "0";
    video.style.width = "2px";
    video.style.height = "2px";
    video.style.opacity = "0";
    video.style.pointerEvents = "none";

    video.onerror = (error) => finishErr(error);

    video.onloadedmetadata = async () => {
      try {
        // Muted play is the decode kick iOS requires (never needs a gesture).
        await video.play().catch(() => {
          // Play can still be refused (rare); the frame-wait below then relies
          // on readyState/loadeddata — better than failing outright.
        });
        await waitForFrameOrTimeout(video, 300);
        if (done) {
          return;
        }
        video.pause();

        // First frame is often black — seek to a representative frame.
        const target = Math.min(
          0.5,
          Number.isFinite(video.duration) ? video.duration / 2 : 0.5,
        );
        if (video.currentTime >= target) {
          finishOk();
          return;
        }
        video.onseeked = async () => {
          await waitForFrameOrTimeout(video, 300);
          finishOk();
        };
        video.currentTime = target;
      } catch (err) {
        finishErr(err);
      }
    };

    document.body.appendChild(video);
    video.src = url;
    video.load();
  });
}

async function createThumbnail(
  source: CanvasImageSource,
  width: number,
  height: number,
  maxWidth: number,
  maxHeight: number,
  quality: number = 0.9,
) {
  const canvas = document.createElement("canvas");
  const { width: newWidth, height: newHeight } = getWidthAndHeight(
    width,
    height,
    maxWidth,
    maxHeight,
  );

  canvas.width = newWidth;
  canvas.height = newHeight;
  const ctx: CanvasRenderingContext2D = canvas.getContext(
    "2d",
  ) as CanvasRenderingContext2D;
  // High-quality resampling: the default smoothing quality is "low", which
  // aliases badly when downscaling large photos. "high" lets the lower WebP
  // quality below hold up with no visible loss at bubble size.
  ctx.imageSmoothingEnabled = true;
  ctx.imageSmoothingQuality = "high";
  ctx.drawImage(source, 0, 0, width, height, 0, 0, newWidth, newHeight);

  return new Promise<Blob | null>((resolve, reject) => {
    if (!canvas) {
      reject(new Error("Canvas is null"));
      return;
    }

    canvas.toBlob(
      (blob) => {
        if (blob) {
          resolve(blob);
        } else {
          reject(new Error("errorCreatingImageThumbnail"));
        }
      },
      "image/webp",
      quality,
    );
  });
}

// Draws the video's current frame scaled into a canvas and reports whether
// the pixels are blank alongside the encoded blob.
async function drawVideoFrame(
  video: HTMLVideoElement,
  maxWidth: number,
  maxHeight: number,
  quality: number,
): Promise<{ blob: Blob | null; blank: boolean }> {
  const canvas = document.createElement("canvas");
  const { width, height } = getWidthAndHeight(
    video.videoWidth,
    video.videoHeight,
    maxWidth,
    maxHeight,
  );
  canvas.width = width;
  canvas.height = height;
  const ctx = canvas.getContext("2d");
  if (!ctx) {
    return { blob: null, blank: true };
  }
  // Match the image path: high-quality downscale of the captured frame.
  ctx.imageSmoothingEnabled = true;
  ctx.imageSmoothingQuality = "high";
  ctx.drawImage(
    video,
    0,
    0,
    video.videoWidth,
    video.videoHeight,
    0,
    0,
    width,
    height,
  );
  const blank = isImageDataBlank(ctx.getImageData(0, 0, width, height).data);
  const blob = await new Promise<Blob | null>((resolve) => {
    canvas.toBlob((b) => resolve(b), "image/webp", quality);
  });
  return { blob, blank };
}

export type CapturedVideoThumbnail = {
  blob: Blob;
  /**
   * The video's native pixel dimensions (post-rotation, as the engine reports
   * them), read off the decoded element the frame was captured from. Absent
   * when the element reported 0×0 (never seen in practice, but a 0 here would
   * poison the message meta downstream).
   */
  width?: number;
  height?: number;
};

export async function createVideoThumbnail(
  file: File,
  maxWidth: number = 200,
  maxHeight: number = 200,
  quality: number = 0.8,
): Promise<CapturedVideoThumbnail | null> {
  if (!ALLOWED_VIDEO_TYPES.includes(file.type)) {
    throw new Error("fileTypeNotSupported");
  }

  const { video, cleanup } = await createVideoFromFile(file);
  try {
    // createVideoFromFile resolves after loadedmetadata, so the native dims
    // are already populated. They ride along with the captured frame — the
    // decode is already paid for, and the send path embeds them in the
    // message meta so bubbles/viewer can size frames exactly on first paint.
    const dims =
      video.videoWidth > 0 && video.videoHeight > 0
        ? { width: video.videoWidth, height: video.videoHeight }
        : {};
    // Verify the capture empirically: a blank draw gets a short wait and a
    // redraw (engines occasionally fire ready events one frame early). After
    // the retries, ACCEPT the frame even if still uniform — a genuinely black
    // scene is a legitimate thumbnail; the placeholder is only for failures.
    let lastBlob: Blob | null = null;
    for (let attempt = 0; attempt < BLANK_FRAME_RETRY_LIMIT; attempt++) {
      const { blob, blank } = await drawVideoFrame(
        video,
        maxWidth,
        maxHeight,
        quality,
      );
      lastBlob = blob ?? lastBlob;
      if (blob && !blank) {
        return { blob, ...dims };
      }
      await waitForFrameOrTimeout(video, 150);
    }
    if (!lastBlob) {
      throw new Error("errorCreatingImageThumbnail");
    }
    return { blob: lastBlob, ...dims };
  } finally {
    cleanup();
  }
}

export async function createImageThumbnail({
  file,
  maxWidth = 200,
  maxHeight = 200,
  quality = 0.8,
}: {
  file: File;
  maxWidth?: number;
  maxHeight?: number;
  quality?: number;
}): Promise<Blob | null> {
  if (!ALLOWED_IMAGE_TYPES.includes(file.type)) {
    throw new Error("fileTypeNotSupported");
  }

  let image: ImageBitmap | null = null;

  try {
    image = await createImageBitmap(file);
    return await createThumbnail(
      image,
      image.width,
      image.height,
      maxWidth,
      maxHeight,
      quality,
    );
  } finally {
    // Clean up resources
    if (image) {
      image.close();
    }

    // Help garbage collection by removing references
    image = null;
  }
}

type ImageMetadata = {
  width: number;
  height: number;
};
export async function getImageMetadata(file: File): Promise<ImageMetadata> {
  if (!ALLOWED_IMAGE_TYPES.includes(file.type)) {
    throw new Error("fileTypeNotSupported");
  }

  let image: ImageBitmap | null = null;

  try {
    image = await createImageBitmap(file);

    return {
      width: image.width,
      height: image.height,
    };
  } finally {
    // Clean up resources
    if (image) {
      image.close();
    }

    // Help garbage collection by removing references
    image = null;
  }
}

/**
 * Neutral placeholder thumbnail — a dark 16:9 tile with a play glyph, drawn
 * on a plain canvas (no media pipeline involved, so it cannot fail the way
 * frame capture can). Used when video frame capture fails: a preview nicety
 * must never block sending the video itself.
 */
export async function createPlaceholderVideoThumbnail(
  width: number = 300,
): Promise<Blob | null> {
  const height = Math.round((width * 9) / 16);
  const canvas = document.createElement("canvas");
  canvas.width = width;
  canvas.height = height;
  const ctx = canvas.getContext("2d");
  if (!ctx) {
    return null;
  }

  ctx.fillStyle = "#26272b";
  ctx.fillRect(0, 0, width, height);

  // Centered play triangle
  const cx = width / 2;
  const cy = height / 2;
  const r = Math.min(width, height) / 5;
  ctx.fillStyle = "#4b4d55";
  ctx.beginPath();
  ctx.moveTo(cx - r * 0.6, cy - r);
  ctx.lineTo(cx - r * 0.6, cy + r);
  ctx.lineTo(cx + r, cy);
  ctx.closePath();
  ctx.fill();

  return new Promise<Blob | null>((resolve) => {
    canvas.toBlob((blob) => resolve(blob), "image/jpeg", 0.8);
  });
}
